import { useQueryClient, useMutation, useQuery, useSuspenseQuery, QueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  PricingPreferences,
  POSTPricingPreferencesRequest,
  Price,
  GETPricesResponse,
  PATCHPricesRequest,
  POSTPricesRequest,
  SwitchPriceInputTypePayload
} from './types'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import {
  FINANCIAL_MODEL_SETTINGS_BASE_KEY,
  REVENUES_BASE_KEY,
  TOTALS_KEY,
  concatQueryKey,
  createQueryKey,
  resetGlobalQueryKey
} from '@/api/query-keys'
import { ORDERS_SUMMARY_KEY } from './orders'

export const PRICES_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`prices`]
})

export const PRICING_PREFERENCES_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`pricing-preferences`]
})

const getPricingPreferences = async (id: string): Promise<PricingPreferences[]> => {
  const response = await axiosInstance.get<PricingPreferences[]>(`/pricing-preferences/${id}`)
  return response.data
}

export const useGetPricingPreferencesQuery = (id: string) =>
  useQuery({
    queryKey: PRICING_PREFERENCES_KEY.concat([id]),
    queryFn: () => getPricingPreferences(id)
  })

const addPricingPreferences = async (id: string, data: POSTPricingPreferencesRequest[]): Promise<PricingPreferences[]> => {
  const response = await axiosInstance.post<PricingPreferences[]>(`/pricing-preferences/${id}`, data)
  return response.data
}

export const useAddPricingPreferencesMutation = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: POSTPricingPreferencesRequest[]) => addPricingPreferences(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRICING_PREFERENCES_KEY })
      queryClient.resetQueries({ queryKey: ORDERS_SUMMARY_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      queryClient.resetQueries({ queryKey: PRICES_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const postPricesBulk = async (financialModelId: string, prices: POSTPricesRequest[]) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/prices/bulk`, {
    periods: prices
  })

  return response.data
}

export const getPrices = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView): Promise<GETPricesResponse> => {
  const response = await axiosInstance.get<GETPricesResponse>(`/financial-models/${financialModelId}/prices/`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return response.data
}

export const useGetPricesQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: concatQueryKey(PRICES_KEY, [financialModelId, period, view ?? `monthly`]),
    queryFn: () => getPrices(financialModelId, period, view)
  })

const addPrice = async (financialModelId: string, data: POSTPricesRequest): Promise<Price> => {
  const response = await axiosInstance.post<Price>(`/financial-models/${financialModelId}/prices`, data)
  return response.data
}

export const getAddPriceMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (data: POSTPricesRequest) => addPrice(financialModelId, data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: PRICES_KEY })
    queryClient.invalidateQueries({ queryKey: ORDERS_SUMMARY_KEY })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const getBulkAddPriceMutationArgs = (financialModelId: string) => ({
  mutationFn: (data: POSTPricesRequest) => addPrice(financialModelId, data)
})

export const useAddPriceMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()

  return useMutation(getAddPriceMutationArgs(financialModelId, queryClient))
}

export const updatePrice = async (financialModelId: string, id: string, data: PATCHPricesRequest): Promise<Price> => {
  const response = await axiosInstance.patch<Price>(`/financial-models/${financialModelId}/prices/${id}`, data)

  return response.data
}

export const getUpdatePriceMutationArgs = (financialModelId: string, id: string, queryClient: QueryClient) => ({
  mutationFn: (data: PATCHPricesRequest) => updatePrice(financialModelId, id, data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: PRICES_KEY })
    queryClient.invalidateQueries({ queryKey: ORDERS_SUMMARY_KEY })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const getBulkUpdatePriceMutationArgs = (financialModelId: string, id: string) => ({
  mutationFn: (data: PATCHPricesRequest) => updatePrice(financialModelId, id, data)
})

export const useUpdatePriceMutation = (financialModelId: string, id: string) => {
  const queryClient = useQueryClient()

  return useMutation(getUpdatePriceMutationArgs(financialModelId, id, queryClient))
}

const switchPriceInputType = async (financialModelId: string, payload: SwitchPriceInputTypePayload) => {
  const response = await axiosInstance.post<Price>(
    `/financial-models/${financialModelId}/prices/${payload.product_id}/config/${payload.column_name}`,
    {},
    {
      params: {
        input_type: payload.input_type,
        year: payload.year,
        month: payload.month,
        customer_segment_id: payload.customer_segment_id
      }
    }
  )
  return response.data
}

export const useSwitchPriceInputTypeMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SwitchPriceInputTypePayload) => switchPriceInputType(financialModelId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRICES_KEY })
      queryClient.invalidateQueries({ queryKey: ORDERS_SUMMARY_KEY })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}
